/**
 * Topic-scoped SecureLink consent for a cmrDataFlow-configured TCI session.
 * Visual parity with ShareHealthRecordsPage (icons, QR, code) — fixed topics only
 * (no patient picker to remove required sections).
 */

import React, { useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ContentCopyRounded,
  PersonRounded,
  LocalHospitalRounded,
  HealingRounded,
  MedicationRounded,
  WarningAmberRounded,
  PeopleRounded,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { AuthContext } from "context/Auth";
import { isDevEnvironment } from "utils/isDevEnvironment";
import {
  buildQrPayload,
  formatManualCode,
} from "services/healthRecordsShareService";
import {
  consentAndRedeemTciSession,
  getTciConsentTopics,
} from "services/cmr/tciSessionConsent";

const SECTION_META = {
  demographics: { icon: PersonRounded, color: "primary" },
  conditions: { icon: LocalHospitalRounded, color: "info" },
  surgical: { icon: HealingRounded, color: "error" },
  medications: { icon: MedicationRounded, color: "success" },
  allergies: { icon: WarningAmberRounded, color: "warning" },
  other: { icon: PeopleRounded, color: "secondary" },
};

/**
 * @param {object} props
 * @param {object} props.tciDoc — tciRegistry doc (with active cmrDataFlow)
 * @param {string} props.tciId
 * @param {string} props.tciSessionId — bound session id for the grant
 * @param {(result: object) => void} props.onConsented — after successful mint+redeem
 * @param {() => void} props.onDeclined — continue TCI without CMR
 * @param {string} [props.tciDisplayName]
 */
const TciSecureLinkConsentScreen = ({
  tciDoc,
  tciId,
  tciSessionId,
  onConsented,
  onDeclined,
  tciDisplayName,
}) => {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [consenting, setConsenting] = useState(false);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);

  const topics = useMemo(() => getTciConsentTopics(tciDoc), [tciDoc]);
  const titleName =
    (typeof tciDisplayName === "string" && tciDisplayName.trim()) ||
    tciDoc?.name ||
    tciId ||
    "this care conversation";

  const handleConsent = useCallback(async () => {
    setConsenting(true);
    setError(null);
    try {
      const result = await consentAndRedeemTciSession({
        user,
        tciDoc,
        tciId,
        tciSessionId,
      });
      setShare({
        manualCode: result.manualCode,
        expiresAtMs: result.expiresAtMs,
      });
      if (typeof onConsented === "function") {
        onConsented(result);
      }
    } catch (err) {
      setError(
        err?.message || "Could not complete SecureLink consent. Please try again.",
      );
    } finally {
      setConsenting(false);
    }
  }, [user, tciDoc, tciId, tciSessionId, onConsented]);

  const handleDecline = useCallback(() => {
    setError(null);
    if (typeof onDeclined === "function") {
      onDeclined();
    }
  }, [onDeclined]);

  const handleCopyCode = useCallback(() => {
    if (!share?.manualCode || !navigator.clipboard) return;
    navigator.clipboard.writeText(formatManualCode(share.manualCode));
  }, [share]);

  const qrValue = share ? buildQrPayload({ manualCode: share.manualCode }) : "";

  return (
    <Box
      data-testid="tci-securelink-consent"
      sx={{ maxWidth: 560, mx: "auto", py: 2 }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        SecureLink consent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Before {titleName} can use your MasterChart (CMR) data, confirm the
        topic(s) below. Consent lasts 30 minutes and applies only to this
        conversation session.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        These topics are fixed for this care conversation — you can consent or
        decline, but you cannot change which topics are included.
      </Alert>

      <Paper variant="outlined" sx={{ mb: 2 }} data-testid="tci-consent-topics">
        {topics.length === 0 ? (
          <Box sx={{ py: 2, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No CMR topics are configured for this conversation.
            </Typography>
          </Box>
        ) : (
          topics.map((topic, i) => {
            const meta = SECTION_META[topic.id] ?? SECTION_META.demographics;
            const Icon = meta.icon;
            const palette = theme.palette[meta.color] ?? theme.palette.primary;
            return (
              <React.Fragment key={topic.id}>
                {i > 0 && <Divider />}
                <Box
                  data-testid={`tci-consent-topic-${topic.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: alpha(palette.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: `${meta.color}.main` }} />
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {topic.label}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })
        )}
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {share && (
        <Box
          data-testid="tci-consent-qr"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              lineHeight: 0,
              bgcolor: "common.white",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <QRCodeSVG value={qrValue} size={180} level="H" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              Code
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" fontWeight={700} letterSpacing={2}>
                {formatManualCode(share.manualCode)}
              </Typography>
              <IconButton onClick={handleCopyCode} aria-label="Copy code">
                <ContentCopyRounded fontSize="small" />
              </IconButton>
            </Stack>
            {isDevEnvironment() && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, fontFamily: "monospace", wordBreak: "break-all" }}
              >
                DEBUG (dev only) — QR payload: {qrValue}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Consent recorded for this session. You can continue.
            </Typography>
          </Box>
        </Box>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <LoadingButton
          variant="contained"
          onClick={handleConsent}
          loading={consenting}
          disabled={!topics.length || !!share}
          data-testid="tci-consent-confirm"
        >
          I consent — use SecureLink
        </LoadingButton>
        <Button
          variant="outlined"
          onClick={handleDecline}
          disabled={consenting}
          data-testid="tci-consent-decline"
        >
          Continue without MasterChart data
        </Button>
      </Stack>
    </Box>
  );
};

TciSecureLinkConsentScreen.propTypes = {
  tciDoc: PropTypes.object,
  tciId: PropTypes.string.isRequired,
  tciSessionId: PropTypes.string.isRequired,
  onConsented: PropTypes.func,
  onDeclined: PropTypes.func,
  tciDisplayName: PropTypes.string,
};

export default TciSecureLinkConsentScreen;
